import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { Card } from "@/components/ui/card";
import { Crown } from "lucide-react";

const PODIO = [
  { ring: "ring-amber-300", bg: "bg-amber-400", av: "h-16 w-16 text-xl", base: "h-[70px]" },
  { ring: "ring-slate-200", bg: "bg-slate-400", av: "h-12 w-12 text-base", base: "h-[50px]" },
  { ring: "ring-orange-200", bg: "bg-orange-700", av: "h-12 w-12 text-base", base: "h-[38px]" },
];

const iniciales = (n) => (n || "?").split(" ").filter(Boolean).slice(0, 2)
  .map((p) => p[0]).join("").toUpperCase();

export const ConjugationLeaderboard = ({ myUserId, refreshKey }) => {
  const { t } = useI18n();
  const [period, setPeriod] = useState("week");
  const [board, setBoard] = useState(null);

  useEffect(() => {
    api.get(`/conjugation/practice/leaderboard?period=${period}`)
      .then((r) => setBoard(r.data)).catch(() => {});
  }, [period, refreshKey]);

  const items = board?.items || [];
  // El podio se dibuja 2 · 1 · 3 para que el primer lugar quede siempre al centro.
  const podio = [items[1], items[0], items[2]];
  const resto = items.slice(3, 8);
  const yo = items.find((r) => r.user_id === myUserId) || board?.me;
  const fueraDeLista = yo && !items.some((r) => r.user_id === myUserId && r.rank <= 8);
  const arriba = yo && yo.rank > 1 ? items.find((r) => r.rank === yo.rank - 1) : null;

  return (
    <Card className="p-5" data-testid="conjugation-leaderboard">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold flex items-center gap-2">
          <Crown className="h-4 w-4 text-amber-500" />
          {period === "week" ? t("cj_rank_week") : t("cj_rank_all_time")}
        </h3>
        <div className="flex gap-1">
          {["week", "all"].map((p) => (
            <button key={p} onClick={() => setPeriod(p)}
                    data-testid={`leaderboard-period-${p}`}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      period === p ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
              {p === "week" ? t("cj_rank_week") : t("cj_rank_all_time")}
            </button>
          ))}
        </div>
      </div>

      {!items.length ? (
        <p className="text-sm text-slate-500 py-4" data-testid="leaderboard-empty">{t("cj_rank_empty")}</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 items-end mt-5" data-testid="leaderboard-podium">
            {podio.map((r, col) => {
              if (!r) return <div key={col} />;
              const i = r.rank - 1;
              const soyYo = r.user_id === myUserId;
              return (
                <div key={r.user_id} className="text-center" data-testid={`podium-${r.rank}`}>
                  {i === 0 && <Crown className="h-4 w-4 text-amber-400 mx-auto mb-1 animate-pulse" />}
                  <span className={`${PODIO[i].av} ${PODIO[i].bg} ring-4 ${PODIO[i].ring} rounded-full
                                    grid place-items-center mx-auto font-bold text-white`}>
                    {iniciales(r.name)}
                  </span>
                  <p className="text-[13px] font-semibold mt-2 truncate">{r.name}</p>
                  {soyYo && (
                    <p className="text-[11px] font-bold text-orange-600 -mt-0.5">{t("cj_rank_you")}</p>
                  )}
                  <p className="text-[11px] text-slate-500">{r.points} · {r.accuracy}%</p>
                  <div className={`${PODIO[i].base} ${PODIO[i].bg} rounded-t-xl mt-2 grid place-items-center
                                   text-white font-bold`}>{r.rank}</div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 space-y-0.5">
            {resto.map((r) => (
              <Row key={r.user_id} r={r} me={r.user_id === myUserId} t={t} />
            ))}
            {fueraDeLista && (
              <>
                <p className="text-center text-xs text-slate-400 py-1">· · ·</p>
                <Row r={yo} me t={t} />
              </>
            )}
          </div>

          <p className="text-xs text-slate-500 mt-3" data-testid="leaderboard-my-rank">
            {arriba && arriba.points > yo.points
              ? t("cj_rank_gap", { n: arriba.points - yo.points, name: arriba.name })
              : board?.my_rank
                ? t("cj_rank_my_place", { n: board.my_rank, total: board.players })
                : t("cj_rank_hint")}
          </p>
        </>
      )}
    </Card>
  );
};

const Row = ({ r, me, t }) => (
  <div data-testid={`leaderboard-row-${r.rank}`}
       className={`flex items-center gap-3 text-sm rounded-lg px-2.5 py-2 ${
         me ? "bg-orange-50 border border-orange-200" : "hover:bg-slate-50"}`}>
    <span className="w-5 text-center text-xs font-bold text-slate-400">{r.rank}</span>
    <span className="font-medium truncate">
      {me ? t("cj_rank_you") : r.name}
    </span>
    <strong className="ml-auto text-amber-600 text-[13px] whitespace-nowrap">
      {r.points} {t("cj_pts")}
    </strong>
  </div>
);
